import React, { useState } from "react";

import {
  Breadcrumbs,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Link,
  makeStyles,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import clsx from "clsx";

import { useHistory } from "react-router";

import LatestReportsTable from "./ReportAdminDashboard/LatestReportsTable";
import Navigation from "../Navigation";
import { userIsFPEmployee } from "../util";
import useFetchLatestReports from "./queries/useFetchLatestReports";
import DomainsSelect from "./ReportAdminDashboard/DomainsSelect";
import useWTMURLs from "./useWTMURLs";

export type ReportAdminDashboardColumn = {
  name: string;
  value: string;
  isSortable: boolean;
  align?: "center" | "inherit" | "justify" | "left" | "right";
  tableCellStyles?: React.CSSProperties;
  labelStyles?: React.CSSProperties;
};

export type WebTrackerReportSortDirection = "asc" | "desc";

export type WebTrackerReportSort = {
  column: "domain" | "startedAt";
  direction: WebTrackerReportSortDirection;
};

export type WebTrackerReportCompleteFilter = "All" | "Complete" | "Incomplete";

const useStyles = makeStyles((theme) => ({
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
  card: {
    width: "100%",
    minHeight: 500,
  },
  cardContent: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  cardHeader: {
    padding: "1em",
  },
  cardNav: {
    padding: "1em",
  },
  formLabel: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  navigation: {
    paddingTop: 12,
  },
  pageTitle: {
    paddingTop: 12,
  },
}));

const columns: ReportAdminDashboardColumn[] = [
  {
    name: "Domain",
    value: "domain",
    isSortable: true,
  },
  {
    name: "Started At",
    value: "startedAt",
    isSortable: true,
    align: "right",
  },
  {
    name: "Visited Pages",
    value: "numVisitedPages",
    isSortable: false,
    align: "right",
  },
  {
    name: "Failed Pages",
    value: "numFailedPages",
    isSortable: false,
    align: "right",
  },
  {
    name: "Completed?",
    value: "isComplete",
    isSortable: false,
    align: "right",
  },
  {
    name: "Internal?",
    value: "internal",
    isSortable: false,
    align: "right",
  },
  {
    name: "Download",
    value: "download",
    isSortable: false,
    align: "right",
  },
];

function ReportAdminDashboard() {
  const classes = useStyles();
  const history = useHistory();
  const wtmURLs = useWTMURLs();

  const [sort, setSort] = useState<WebTrackerReportSort>({
    column: "domain",
    direction: "asc",
  });

  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [page, setPage] = useState(0);
  const [domain, setDomain] = useState<string | null>(null);
  const [completeFilter, setCompleteFilter] = useState<
    WebTrackerReportCompleteFilter
  >("Complete");

  const onSort = (clickedColumn: string) => {
    const column = columns.find((c) => c.value === clickedColumn);
    if (!column || !column.isSortable) {
      return;
    }

    if (sort.column === column.value) {
      setSort({
        column: column.value,
        direction: sort.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setSort({
        column: column.value as "domain" | "startedAt",
        direction: "asc",
      });
    }
  };

  const {
    data: latestReportsData,
    isLoading: latestReportsLoading,
  } = useFetchLatestReports(
    completeFilter,
    domain,
    rowsPerPage,
    page,
    sort.column,
    sort.direction
  );

  if (!userIsFPEmployee()) {
    history.push(wtmURLs.trackersOverview);
    return null;
  }

  return (
    <Navigation>
      <div className={classes.pageTitle}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className={clsx(classes.breadcrumb)}
        >
          <Link color="inherit">
            <Typography variant="overline">Web Trackers</Typography>
          </Link>
          <Typography variant="overline">Report Admin</Typography>
        </Breadcrumbs>
        <Typography component="h1" gutterBottom variant="h3">
          Report Admin
        </Typography>
      </div>

      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title="Latest Reports for All Domains"
        />

        <Divider />

        <CardContent className={classes.cardContent}>
          <nav className={classes.cardNav}>
            <Grid container xs={12} spacing={2}>
              <Grid item xs={8}>
                <FormControl fullWidth>
                  <FormLabel className={classes.formLabel}>
                    Filter by Domain
                  </FormLabel>
                  <DomainsSelect
                    selectedDomain={domain}
                    onChange={(d) => setDomain(d)}
                  />
                </FormControl>
              </Grid>

              <Grid item>
                <FormControl fullWidth size="small">
                  <FormLabel className={classes.formLabel}>
                    Filter by Completion Status
                  </FormLabel>

                  <Select
                    multiple={false}
                    value={completeFilter}
                    variant="outlined"
                    onChange={(e) =>
                      setCompleteFilter(
                        e.target.value as WebTrackerReportCompleteFilter
                      )
                    }
                  >
                    <MenuItem value="All">All Scans</MenuItem>
                    <MenuItem value="Complete">Complete Scans Only</MenuItem>
                    <MenuItem value="Incomplete">
                      Incomplete Scans Only
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </nav>

          {latestReportsLoading ? (
            <CircularProgress />
          ) : (
            <LatestReportsTable
              columns={columns}
              sort={sort}
              onSort={onSort}
              reports={latestReportsData?.reports ?? []}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={setPage}
              onRowsPerPageChange={(newRowsPerPage) => {
                setRowsPerPage(newRowsPerPage);
                setPage(0);
              }}
              count={latestReportsData?.count ?? 0}
            />
          )}
        </CardContent>
      </Card>
    </Navigation>
  );
}

export default ReportAdminDashboard;
